// Structural data for the marketing site. All user-facing strings live in
// frontend/public/locales/<lang>/landing.json and are resolved via i18next
// using the `key` references below.

import { GOOGLE_APP_INTEGRATIONS } from '../lib/constants';

// Share token of the public "BusyBee Public" team that hosts the live
// Changelog / Roadmap / Ideaboard portals. Hash-routed inside PublicTeamPage.
const PUBLIC_TEAM_TOKEN = '7L106DYwv0eDJxkOlQ9GRzup9W8mfMBd';
const publicPortal = (hash) => `/public/team/${PUBLIC_TEAM_TOKEN}#${hash}`;

export const navLinks = {
  product: {
    // Four groups of five, so the product menu stays short enough for a laptop screen.
    groups: [
      {
        titleKey: 'nav.groups.tasks_projects',
        items: [
          { key: 'task_management', icon: 'tasks', color: 'blue', path: '/features/task-management' },
          { key: 'projects', icon: 'layout', color: 'yellow', path: '/features/projects' },
          { key: 'calendar', icon: 'calendar', color: 'blue', path: '/features/calendar' },
          { key: 'custom_fields', icon: 'sliders', color: 'purple', path: '/features/custom-fields' },
          { key: 'recurring_tasks', icon: 'repeat', color: 'blue', path: '/features/recurring-tasks' },
        ],
      },
      {
        titleKey: 'nav.groups.workspace',
        items: [
          { key: 'documents', icon: 'file-text', color: 'yellow', path: '/features/documents' },
          { key: 'whiteboards', icon: 'pencil', color: 'yellow', path: '/features/whiteboards' },
          { key: 'databases', icon: 'database', color: 'cyan', path: '/features/databases' },
          { key: 'mindmaps', icon: 'network', color: 'purple', path: '/features/mindmaps' },
          { key: 'search', icon: 'search', color: 'blue', path: '/features/search' },
        ],
      },
      {
        titleKey: 'nav.groups.focus_schedule',
        items: [
          { key: 'focus_time', icon: 'focus', color: 'blue', path: '/features/focus-time' },
          { key: 'habits', icon: 'habits', color: 'yellow', path: '/features/habits' },
          { key: 'buffer_time', icon: 'coffee', color: 'pink', path: '/features/buffer-time' },
          { key: 'analytics', icon: 'trending-up', color: 'yellow', path: '/features/analytics' },
          { key: 'notifications', icon: 'bell', color: 'pink', path: '/features/notifications' },
        ],
      },
      {
        titleKey: 'nav.groups.collaborate_automate',
        items: [
          { key: 'team_collaboration', icon: 'users', color: 'pink', path: '/features/team-collaboration' },
          { key: 'public_sharing', icon: 'globe', color: 'green', path: '/features/public-sharing' },
          { key: 'desktop_app', icon: 'monitor', color: 'blue', path: '/download/desktop' },
          { key: 'mobile_app', icon: 'smartphone', color: 'blue', path: '/download/mobile' },
          { key: 'languages', icon: 'languages', color: 'purple', path: '/features/languages' },
        ],
      },
    ],
    integrations: [
      ...(GOOGLE_APP_INTEGRATIONS ? [
        { key: 'google_calendar_short', icon: 'calendar', path: '/integrations/google-calendar' },
        { key: 'gmail_short', icon: 'mail', path: '/integrations/gmail' },
      ] : []),
      { key: 'webhooks_short', icon: 'webhook', path: '/integrations/webhooks' },
      { key: 'mcp_short', icon: 'sparkles', path: '/integrations/mcp' },
    ],
    // Smaller links under the integrations
    more: [
      { key: 'trash_restore', icon: 'trash', path: '/features/trash-restore' },
    ],
  },
  teams: [
    { key: 'product', icon: 'box', color: 'blue', path: '/teams/product' },
    { key: 'sales', icon: 'trending-up', color: 'pink', path: '/teams/sales' },
    { key: 'engineering', icon: 'code', color: 'yellow', path: '/teams/engineering' },
    { key: 'people_ops', icon: 'heart', color: 'yellow', path: '/teams/people-ops' },
    { key: 'marketing', icon: 'megaphone', color: 'blue', path: '/teams/marketing' },
    { key: 'finance', icon: 'dollar-sign', color: 'pink', path: '/teams/finance' },
    { key: 'assistants', icon: 'briefcase', color: 'pink', path: '/teams/assistants' },
  ],
  resources: [
    { key: 'blog', icon: 'file-text', color: 'blue', path: '/blog' },
    { key: 'help', icon: 'help-circle', color: 'pink', path: '/help' },
    { key: 'glossary', icon: 'book', color: 'pink', path: '/glossary' },
    { key: 'focus_timer', icon: 'timer', color: 'pink', path: '/tools/focus-timer' },
  ],
};

// Items shown specifically in the Solutions and Resources megamenus (slightly
// different ordering and copy than the footer/sitemap lists above).
export const teamItems = [
  { key: 'product', icon: 'box', color: 'pink', path: '/teams/product' },
  { key: 'engineering', icon: 'code', color: 'yellow', path: '/teams/engineering' },
  { key: 'sales', icon: 'trending-up', color: 'pink', path: '/teams/sales' },
  { key: 'marketing', icon: 'megaphone', color: 'blue', path: '/teams/marketing' },
  { key: 'people_ops', icon: 'heart', color: 'yellow', path: '/teams/people-ops' },
  { key: 'finance', icon: 'dollar-sign', color: 'blue', path: '/teams/finance' },
  { key: 'assistants', icon: 'briefcase', color: 'pink', path: '/teams/assistants' },
];

export const resourceItems = [
  { key: 'blog',        icon: 'file-text',   color: 'blue',   path: '/blog',                     group: 'learn' },
  { key: 'help',        icon: 'help-circle', color: 'pink',   path: '/help',                     group: 'learn' },
  { key: 'glossary',    icon: 'book',        color: 'pink',   path: '/glossary',                 group: 'learn' },
  { key: 'developers',  icon: 'code',        color: 'yellow', path: '/developers',               group: 'learn' },
  { key: 'changelog',   icon: 'history',     color: 'yellow', path: publicPortal('changelog'),   group: 'updates' },
  { key: 'roadmap',     icon: 'map',         color: 'blue',   path: publicPortal('roadmap'),     group: 'updates' },
  { key: 'ideaboard',   icon: 'lightbulb',   color: 'pink',   path: publicPortal('ideaboard'),   group: 'updates' },
  { key: 'about',       icon: 'heart',       color: 'pink',   path: '/about',                    group: 'company' },
  { key: 'focus_timer', icon: 'timer',       color: 'pink',   path: '/tools/focus-timer',        group: 'tools' },
];

// Competitor comparison pages — shown under Solutions in the megamenu so
// users coming from Airtable / Notion / ClickUp / Monday can find the side-
// by-side comparison in one click.
export const alternativeItems = [
  { key: 'chatgpt_dots', icon: 'sparkles', color: 'green', path: '/alternatives/chatgpt-dots' },
  { key: 'notion',   icon: 'file-text', color: 'pink', path: '/alternatives/notion' },
  { key: 'clickup',  icon: 'layout', color: 'purple', path: '/alternatives/clickup' },
  { key: 'asana',    icon: 'users', color: 'pink', path: '/alternatives/asana' },
  { key: 'motion',   icon: 'calendar', color: 'blue', path: '/alternatives/motion' },
  { key: 'lindy',    icon: 'bot', color: 'yellow', path: '/alternatives/lindy' },
  { key: 'todoist',  icon: 'tasks', color: 'pink', path: '/alternatives/todoist' },
  { key: 'trello',   icon: 'layout', color: 'blue', path: '/alternatives/trello' },
  { key: 'airtable', icon: 'table-2', color: 'blue', path: '/alternatives/airtable' },
  { key: 'monday',   icon: 'sliders', color: 'yellow', path: '/alternatives/monday' },
];

export const securityFeatures = [
  { key: 'approvals',    icon: 'shield' },
  { key: 'connections',  icon: 'users' },
  { key: 'no_training',  icon: 'message-circle' },
  { key: 'encryption',   icon: 'lock' },
  { key: 'eu_hosting',   icon: 'activity' },
  { key: 'controls',     icon: 'key' },
];

export const faqs = [
  { key: 'what_is' },
  { key: 'busybee' },
  { key: 'without_bots' },
  { key: 'bot_vs_chat' },
  { key: 'away' },
  { key: 'safe' },
  { key: 'free' },
  { key: 'credits' },
  { key: 'models' },
  { key: 'apps' },
  { key: 'data' },
  { key: 'features' },
  { key: 'team' },
];

export const footerLinks = {
  products: [
    { key: 'task_management',     path: '/features/task-management' },
    { key: 'projects',            path: '/features/projects' },
    { key: 'calendar_planner',    path: '/features/calendar' },
    { key: 'focus_time',          path: '/features/focus-time' },
    { key: 'habits',              path: '/features/habits' },
    { key: 'documents',           path: '/features/documents' },
    { key: 'team_collaboration',  path: '/features/team-collaboration' },
    { key: 'analytics',           path: '/features/analytics' },
    ...(GOOGLE_APP_INTEGRATIONS ? [{ key: 'gmail_integration', path: '/features/gmail-integration' }] : []),
    { key: 'webhooks',            path: '/features/webhooks' },
    { key: 'buffer_time',         path: '/features/buffer-time' },
    { key: 'databases',           path: '/features/databases' },
    { key: 'desktop_app',         path: '/download/desktop' },
    { key: 'mobile_app',          path: '/download/mobile' },
    { key: 'languages',           path: '/features/languages' },
  ],
  pricing: [
    { key: 'plans',      path: '/pricing' },
    { key: 'credits',    path: '/ai-bots/models' },
    { key: 'enterprise', path: '/enterprise' },
  ],
  company: [
    { key: 'about',   path: '/about' },
    { key: 'careers', path: '/careers' },
    { key: 'contact', path: '/contact' },
    { key: 'press',   path: '/press' },
  ],
  resources: [
    { key: 'changelog',    path: publicPortal('changelog') },
    { key: 'roadmap',      path: publicPortal('roadmap') },
    { key: 'ideaboard',    path: publicPortal('ideaboard') },
    { key: 'blog',         path: '/blog' },
    { key: 'help',         path: '/help' },
    { key: 'developers',   path: '/developers' },
  ],
  legal: [
    { key: 'privacy',  path: '/privacy' },
    { key: 'terms',    path: '/terms' },
    { key: 'security', path: '/security' },
    { key: 'cookies',  path: '/cookies' },
    { key: 'ai_policy', path: '/ai-policy' },
    { key: 'subprocessors', path: '/subprocessors' },
    { key: 'delete_account', path: '/delete-account' },
  ],
};
